import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import {
	Info,
	Search,
	CheckCircle2,
	ChevronDown,
	Loader2,
	ExternalLink,
	CalendarDays
} from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import {
	Command,
	CommandInput,
	CommandList,
	CommandEmpty,
	CommandGroup,
	CommandItem
} from './ui/command';
import { analytics } from '../services/analytics';
import { cn } from '../lib/utils';
import ContactSupportModal from './Modals/ContactSupportModal';
import { I18nContext } from '../context/translations/TranslationsContext.context';
import { ArcaContext } from '../context/ArcaContext.context';
import {
	getPensionIsinCatalog,
	createPensionPlanTransfer,
	contactExpert
} from '../api/pension-plan-transfers';
import startTransferSignatureKyc from '../api/kyc/startTransferSignatureKyc';
import { resolveSignableTransferId } from '../api/pension-plan-transfers/resolveSignableTransferId';
import { irAUnaWebDeFuera } from '../movil/navegadorExterno';
import { origenPublico } from '../movil/origenPublico';
import { buildCreateTransferPayload } from '../api/pension-plan-transfers/buildCreateTransferPayload';
import { isDemoAccountNoticeOpen } from '../utils/demoAccountInterceptor';
import {
	CreatePensionPlanTransferDto,
	PensionIsinCatalog
} from '../api/pension-plan-transfers/interfaces/PensionPlanTransfer';

interface PensionPlanTransferJourneyV2Props {
	isOpen: boolean;
	onClose: () => void;
}

// ─── Combobox with search ───────────────────────────────────────────
interface ComboboxOption {
	id: string;
	name: string;
	DGSFP_code?: string;
}

const SearchableCombobox: React.FC<{
	label: string;
	placeholder: string;
	searchPlaceholder: string;
	emptyText: string;
	options: ComboboxOption[];
	value: string;
	onChange: (value: string) => void;
}> = ({ label, placeholder, searchPlaceholder, emptyText, options, value, onChange }) => {
	const [open, setOpen] = useState(false);
	const selected = options.find((o) => o.id === value);

	return (
		<div className="space-y-1.5">
			<Label className="text-sm font-medium">{label}</Label>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						aria-expanded={open}
						className="w-full justify-between font-normal"
					>
						<span className="truncate">{selected?.name || placeholder}</span>
						<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
					<Command>
						<CommandInput placeholder={searchPlaceholder} />
						<CommandList>
							<CommandEmpty>{emptyText}</CommandEmpty>
							<CommandGroup>
								{options.map((opt) => (
									<CommandItem
										key={opt.id}
										value={opt.name}
										onSelect={() => {
											onChange(opt.id);
											setOpen(false);
										}}
									>
										{opt.name}
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
		</div>
	);
};

// ─── Main component ─────────────────────────────────────────────────
const PensionPlanTransferJourneyV2: React.FC<PensionPlanTransferJourneyV2Props> = ({
	isOpen,
	onClose
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token } = useContext(ArcaContext);

	// Toast replacement — simple inline messages
	const [toastMsg, setToastMsg] = useState<{
		text: string;
		type: 'success' | 'error' | 'warning';
	} | null>(null);
	const showToast = (text: string, type: 'success' | 'error' | 'warning' = 'error') => {
		// Cuenta demo: su aviso único ya está abierto (DemoAccountBlockedNotice), sin otro debajo.
		if (isDemoAccountNoticeOpen()) return;
		setToastMsg({ text, type });
		setTimeout(() => setToastMsg(null), 5000);
	};

	// DGSFP code explanation
	const dgsfpCodeExplanation = (
		<>
			{translate('pensionTransfer.dgsfpExplanation')}{' '}
			<a
				href="https://rrpp.dgsfp.mineco.es/Plan"
				target="_blank"
				rel="noopener noreferrer"
				className="text-secondary underline font-medium hover:text-secondary/80"
			>
				{translate('pensionTransfer.dgsfpLink')}
			</a>
			.
		</>
	);

	// Transfer flow state
	const [transferStep, setTransferStep] = useState(1);
	const [selectedEntity, setSelectedEntity] = useState('');
	const [selectedFund, setSelectedFund] = useState('');
	const [selectedPlan, setSelectedPlan] = useState('');
	const [selectedDGSFPcode, setSelectedDGSFPcode] = useState('');
	const [showIsinHelp, setShowIsinHelp] = useState(false);
	const [isInSearchMode, setIsInSearchMode] = useState(false);
	const [expertInfo, setExpertInfo] = useState('');
	const [showExpertForm, setShowExpertForm] = useState(false);
	const [isTooltipOpen, setIsTooltipOpen] = useState(false);
	const [requestId, setRequestId] = useState<string>('');
	/**
	 * UUID de la petición recién creada. Se guarda aparte del `requestId` de
	 * negocio porque la firma del boletín cuelga de la FILA, no de la referencia
	 * que se le enseña al cliente.
	 */
	const [createdTransferId, setCreatedTransferId] = useState<string | null>(null);
	const [signingBulletin, setSigningBulletin] = useState(false);
	const tooltipRef = useRef<HTMLDivElement>(null);

	const [searchResults, setSearchResults] = useState<
		Array<{
			entity: string;
			fund: string;
			plan: string;
			DGSFP_code: string;
			catalogId: string;
			planName: string;
			denomination: string;
		}>
	>([]);

	// Catalog state
	const [catalogData, setCatalogData] = useState<PensionIsinCatalog[]>([]);
	const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);

	// Contact support modal
	const [isContactSupportModalOpen, setIsContactSupportModalOpen] = useState(false);

	// Track transfer_plan_page_viewed solo en la transición false→true de isOpen.
	const wasOpenRef = useRef(false);
	useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			analytics.transferPlanPageViewed({ currentUrl: window.location.pathname });
		}
		wasOpenRef.current = isOpen;
	}, [isOpen]);

	// Track transfer_plan_fund_selected en la transición ''→<fundName>.
	const prevFundRef = useRef('');
	useEffect(() => {
		if (selectedFund && selectedFund !== prevFundRef.current) {
			analytics.transferPlanFundSelected({ fundName: selectedFund });
		}
		prevFundRef.current = selectedFund;
	}, [selectedFund]);

	// Click outside tooltip
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
				setIsTooltipOpen(false);
			}
		};
		if (isTooltipOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isTooltipOpen]);

	// Catalog helpers
	const getEntitiesFromCatalog = () => {
		const entities = new Map<string, string>();
		catalogData.forEach((item) => entities.set(item.entityName, item.entityName));
		return Array.from(entities.entries()).map(([id, name]) => ({ id, name }));
	};

	const getFundsFromCatalog = (entityName: string) => {
		const funds = new Map<string, string>();
		catalogData
			.filter((item) => item.entityName === entityName)
			.forEach((item) => funds.set(item.fundName, item.fundName));
		return Array.from(funds.entries()).map(([id, name]) => ({ id, name }));
	};

	const getPlansFromCatalog = (entityName: string, fundName: string) => {
		return catalogData
			.filter((item) => item.entityName === entityName && item.fundName === fundName)
			.map((item) => ({ id: item.id, name: item.planName, DGSFP_code: item.DGSFP_code }));
	};

	// Search DGSFP code
	const searchDgsfpCodeInAllData = (searchTerm: string) => {
		const normalizedSearch = searchTerm
			.toLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '');

		return catalogData
			.filter((item) => {
				const fields = [
					item.DGSFP_code,
					item.planName,
					item.entityName,
					item.fundName,
					item.denomination,
					item.dgsfpCodeGeneric
				];
				return fields.some((f) =>
					f
						?.toLowerCase()
						.normalize('NFD')
						.replace(/[\u0300-\u036f]/g, '')
						.includes(normalizedSearch)
				);
			})
			.map((item) => ({
				entity: item.entityName,
				fund: item.fundName,
				plan: item.planName,
				DGSFP_code: item.DGSFP_code,
				catalogId: item.id,
				planName: item.planName,
				denomination: item.denomination || ''
			}));
	};

	// Load catalog
	const loadCatalog = async () => {
		if (!token) return;
		setIsLoadingCatalog(true);
		try {
			const response = await getPensionIsinCatalog(token);
			if (response.success && response.data) {
				setCatalogData(response.data);
			}
		} catch (error) {
			console.error('Error loading catalog:', error);
			showToast(translate('pensionPlan.transfer.catalogErrorDescription'));
		} finally {
			setIsLoadingCatalog(false);
		}
	};

	useEffect(() => {
		if (isOpen) loadCatalog();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, token]);

	// Handlers
	const handleTransferContinue = async () => {
		if (transferStep === 1) {
			setTransferStep(2);
		} else if (transferStep === 2) {
			if (selectedDGSFPcode) {
				if (!selectedPlan) {
					const catalogItem = catalogData.find((item) => item.DGSFP_code === selectedDGSFPcode);
					if (catalogItem) {
						setSelectedPlan(catalogItem.id);
					} else {
						showToast(translate('pensionPlan.transfer.dgsfpNotFound'));
						return;
					}
				}
				setTransferStep(4);
			} else {
				showToast(translate('pensionPlan.transfer.dgsfpRequired'));
			}
		} else if (transferStep === 4) {
			const selectedCatalogItem = catalogData.find((item) => item.DGSFP_code === selectedDGSFPcode);
			const transferData = {
				entity: {
					id: selectedEntity,
					name: selectedCatalogItem?.entityName || translate('pensionTransfer.unknown.entity')
				},
				fund: {
					id: selectedFund,
					name: selectedCatalogItem?.fundName || translate('pensionTransfer.unknown.fund')
				},
				plan: {
					id: selectedPlan,
					name: selectedCatalogItem?.planName || translate('pensionTransfer.unknown.plan')
				},
				dgsfpCode: selectedDGSFPcode,
				value: 0,
				transferDate: new Date().toISOString(),
				requestId: `TR-${new Date().getFullYear()}-${String(Date.now()).slice(-5)}`
			};

			try {
				if (!selectedPlan || selectedPlan.trim() === '') {
					showToast(translate('pensionPlan.transfer.planNotIdentified'));
					return;
				}

				const createTransferData: CreatePensionPlanTransferDto = buildCreateTransferPayload({
					catalogId: selectedPlan,
					dgsfpCode: selectedDGSFPcode,
					requestId: transferData.requestId,
					userAgent: navigator.userAgent,
					origin: {
						entity: transferData.entity,
						fund: transferData.fund,
						plan: transferData.plan
					},
					now: new Date().toISOString()
				});

				const response = await createPensionPlanTransfer(token, createTransferData);
				if (response.success) {
					analytics.transferPlanStarted({
						dgsfpCode: selectedDGSFPcode
					});
					analytics.transferPlanCompleted({
						fundSelected: selectedFund,
						dgsfpCode: selectedDGSFPcode
					});
					const backendRequestId = response.data?.requestId || transferData.requestId;
					setRequestId(backendRequestId);
					setCreatedTransferId(resolveSignableTransferId(response.data));
					setTransferStep(5);
				} else {
					const errorMessage = response.message || '';
					if (errorMessage.includes('TRANSFER_ALREADY_EXISTS')) {
						showToast(translate('pensionTransfer.error.duplicateTransfer'), 'warning');
					} else {
						showToast(translate('pensionTransfer.error.createFailed'));
					}
				}
			} catch (error) {
				console.error('Error creating transfer:', error);
				showToast(translate('pensionTransfer.step2.sendErrorDescription'));
			}
		}
	};

	const resetTransferStates = () => {
		setTransferStep(1);
		setSelectedEntity('');
		setSelectedFund('');
		setSelectedPlan('');
		setSelectedDGSFPcode('');
		setShowIsinHelp(false);
		setIsInSearchMode(false);
		setSearchResults([]);
		setShowExpertForm(false);
		setExpertInfo('');
		setRequestId('');
		setToastMsg(null);
	};

	/**
	 * Firmar el boletín aquí mismo, sin pasar por el dashboard.
	 *
	 * Es el momento natural: el cliente acaba de pedir el traspaso y el papel
	 * que lo formaliza es lo siguiente que hay que hacer. El paso del dashboard
	 * se queda como red para quien cierre el modal sin firmar.
	 */
	const handleSignBulletin = useCallback(async () => {
		if (!token || !createdTransferId || signingBulletin) return;
		setSigningBulletin(true);
		try {
			const returnUrl = `${origenPublico()}${window.location.pathname}`;
			const res = await startTransferSignatureKyc(token, {
				transferId: createdTransferId,
				returnUrl
			});
			const url = res.data?.url;
			if (!url) throw new Error('sin url');
			irAUnaWebDeFuera(url, () => setSigningBulletin(false));
		} catch (error) {
			console.error('Error starting bulletin signature:', error);
			setSigningBulletin(false);
			showToast(
				translate('pensionTransfer.step5.signError')
			);
		}
	}, [token, createdTransferId, signingBulletin, showToast, translate]);

	const handleTransferComplete = () => {
		onClose();
		resetTransferStates();
	};

	const handleTransferModalClose = () => {
		// Si se cierra antes de llegar al step 5 (confirmación), es un abandono.
		if (transferStep !== 5) {
			analytics.transferPlanAbandoned({ stepAtAbandon: transferStep });
		}
		onClose();
		resetTransferStates();
	};

	// Step indicator
	const stepLabels = [1, 2, 4, 5];
	const currentStepIdx = stepLabels.indexOf(transferStep);

	return (
		<>
			<Dialog open={isOpen} onOpenChange={(open) => !open && handleTransferModalClose()}>
				<DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>{translate('pensionTransfer.modal.title')}</DialogTitle>
						<DialogDescription className="sr-only">
							{translate('pensionTransfer.step1.description')}
						</DialogDescription>
					</DialogHeader>

					{/* Toast message */}
					{toastMsg && (
						<div
							className={cn(
								'rounded-md border p-3 text-sm',
								toastMsg.type === 'error' &&
									'border-destructive/30 bg-destructive/5 text-destructive',
								toastMsg.type === 'warning' && 'border-yellow-300 bg-yellow-50 text-yellow-800',
								toastMsg.type === 'success' && 'border-primary/20 bg-primary/5 text-primary'
							)}
						>
							{toastMsg.text}
						</div>
					)}

					{/* Step progress */}
					<div className="flex items-center gap-2 mb-2">
						{[1, 2, 3, 4].map((s) => (
							<div
								key={s}
								className={cn(
									'h-1.5 flex-1 rounded-full transition-colors',
									s <= currentStepIdx + 1 ? 'bg-primary' : 'bg-muted'
								)}
							/>
						))}
					</div>

					<div className="space-y-6">
						{/* Step 1 — Intro */}
						{transferStep === 1 && (
							<div className="text-center space-y-4 py-4">
								<div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
									<Info className="w-7 h-7 text-blue-600" />
								</div>
								<h3 className="text-lg font-semibold">
									{translate('pensionTransfer.step1.title')}
								</h3>
								<p className="text-sm text-muted-foreground">
									{translate('pensionTransfer.step1.description')}
								</p>
								<Button
									variant="outline"
									className="rounded-full border-secondary text-secondary hover:bg-secondary/5"
									onClick={() => setIsContactSupportModalOpen(true)}
								>
									{translate('pensionTransfer.step1.helpButton')}
								</Button>
							</div>
						)}

						{/* Step 2 — Plan selection */}
						{transferStep === 2 && (
							<div className="space-y-4">
								<div className="text-center">
									<Button
										variant="outline"
										size="sm"
										className="rounded-full border-secondary text-secondary hover:bg-secondary/5"
										onClick={() => setIsContactSupportModalOpen(true)}
									>
										{translate('pensionTransfer.step1.helpButton')}
									</Button>
								</div>

								{/* DGSFP code input */}
								<div className="space-y-1.5">
									<div className="flex items-center gap-2">
										<Label className="text-sm font-medium">
											{translate('pensionTransfer.step2.knowDgsfp')}
										</Label>
										<div className="relative" ref={tooltipRef}>
											<button
												type="button"
												onClick={() => setIsTooltipOpen(!isTooltipOpen)}
												className="text-secondary hover:text-secondary/80"
											>
												<Info className="w-4 h-4" />
											</button>
											{isTooltipOpen && (
												<div className="absolute top-full left-0 z-50 mt-2 w-80 rounded-md border bg-popover p-3 shadow-lg text-sm">
													{dgsfpCodeExplanation}
												</div>
											)}
										</div>
									</div>
									<div className="flex gap-2">
										<Input
											placeholder={translate('pensionPlan.transfer.dgsfpPlaceholder')}
											value={selectedDGSFPcode}
											onChange={(e) => setSelectedDGSFPcode(e.target.value)}
										/>
										<Button
											size="icon"
											className="shrink-0 bg-primary hover:bg-primary/90"
											onClick={() => {
												if (selectedDGSFPcode.trim()) {
													const results = searchDgsfpCodeInAllData(selectedDGSFPcode);
													setSearchResults(results);
													setIsInSearchMode(true);
												}
											}}
										>
											<Search className="w-4 h-4" />
										</Button>
									</div>
								</div>

								{/* Search results */}
								{isInSearchMode && searchResults.length > 0 && (
									<div className="rounded-lg border border-border bg-card p-4 space-y-2">
										<p className="text-sm font-semibold">
											{translate('pensionTransfer.step2.searchResults')}
										</p>
										<div className="space-y-2 max-h-60 overflow-y-auto">
											{searchResults.map((result, index) => (
												<button
													key={index}
													type="button"
													onClick={() => {
														setSelectedDGSFPcode(result.DGSFP_code);
														setSelectedPlan(result.catalogId);
														setIsInSearchMode(false);
														setSearchResults([]);
													}}
													className="w-full text-left rounded-md border border-border p-3 hover:border-primary/30 hover:bg-primary/5 transition-colors"
												>
													<p className="text-sm font-medium">{result.plan}</p>
													<p className="text-xs text-muted-foreground">
														{result.entity} - {result.fund}
													</p>
													{result.denomination && (
														<p className="text-xs text-muted-foreground">{result.denomination}</p>
													)}
													<p className="text-xs font-semibold text-secondary mt-1">
														{translate('pensionTransfer.step2.dgsfpLabel')} {result.DGSFP_code}
													</p>
												</button>
											))}
										</div>
									</div>
								)}

								{isInSearchMode && searchResults.length === 0 && (
									<div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
										<p className="text-sm text-destructive">
											{translate('pensionTransfer.step2.noResults')} &quot;
											{selectedDGSFPcode}&quot;. {translate('pensionTransfer.step2.tryAnother')}
										</p>
									</div>
								)}

								{/* Find DGSFP toggle */}
								<button
									type="button"
									className="text-sm text-secondary hover:text-secondary/80 hover:underline"
									onClick={() => setShowIsinHelp(!showIsinHelp)}
								>
									{translate('pensionTransfer.step2.findDgsfp')}
								</button>

								{/* Tips */}
								{showIsinHelp && (
									<div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
										<p className="text-sm font-semibold">
											{translate('pensionTransfer.step2.tipsTitle')}
										</p>
										<ol className="space-y-2 text-sm list-decimal list-inside text-foreground">
											<li>{translate('pensionTransfer.step2.tip1')}</li>
											<li>{translate('pensionTransfer.step2.tip2')}</li>
											<li>{translate('pensionPlan.transfer.expertHelp')}</li>
											<li>
												{translate('pensionTransfer.step2.tip3')}{' '}
												<a
													href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3QGXlY8LM-oKA7_WEs3R46zuaInobnNGJFy7XD0j-FoTHigGXUGlrYKGKmKyVXqt6g2JztGdCL"
													target="_blank"
													rel="noopener noreferrer"
													className="inline-flex items-center gap-1 text-secondary hover:underline font-medium"
												>
													<CalendarDays className="w-3.5 h-3.5" />
													{translate('pensionTransfer.step2.tip4')}
												</a>
											</li>
										</ol>
									</div>
								)}

								{/* Expert help toggle */}
								<button
									type="button"
									className="text-sm text-secondary hover:text-secondary/80 hover:underline"
									onClick={() => setShowExpertForm(!showExpertForm)}
								>
									{translate('pensionTransfer.step2.expertInfo')}
								</button>

								{showExpertForm && (
									<div className="rounded-lg border border-border bg-card p-4 space-y-3">
										<Label className="text-sm font-medium">
											{translate('pensionTransfer.step2.describeSituation')}
										</Label>
										<textarea
											value={expertInfo}
											onChange={(e) => setExpertInfo(e.target.value)}
											placeholder={translate('pensionTransfer.step2.expertPlaceholder')}
											rows={4}
											className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
										/>
										<Button
											className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
											onClick={async () => {
												if (expertInfo.trim().length < 10) {
													showToast(translate('pensionTransfer.step2.textTooShortDescription'));
													return;
												}
												try {
													const response = await contactExpert(token, {
														message: expertInfo.trim()
													});
													if (response.success) {
														showToast(
															translate('pensionTransfer.step2.infoSentDescription'),
															'success'
														);
														setExpertInfo('');
														setShowExpertForm(false);
													} else {
														throw new Error('Server error');
													}
												} catch (error) {
													console.error('Error enviando información al experto:', error);
													showToast(translate('pensionTransfer.step2.sendErrorDescription'));
												}
											}}
										>
											{translate('pensionTransfer.step2.sendToExpert')}
										</Button>
									</div>
								)}

								{/* Guided search via selects */}
								{showIsinHelp && (
									<div className="rounded-lg border border-border bg-card p-4 space-y-4">
										<h4 className="text-sm font-semibold">
											{translate('pensionTransfer.step2.howToFind')}
										</h4>
										{isLoadingCatalog ? (
											<div className="flex items-center justify-center py-4">
												<Loader2 className="w-5 h-5 animate-spin text-primary mr-2" />
												<span className="text-sm text-muted-foreground">
													{translate('pensionTransfer.step2.loadingCatalog')}
												</span>
											</div>
										) : (
											<>
												<SearchableCombobox
													label={translate('pensionTransfer.step2.entityLabel')}
													placeholder={translate('pensionTransfer.step2.entityPlaceholder')}
													searchPlaceholder={translate('pensionTransfer.step2.entitySearch')}
													emptyText={translate('pensionTransfer.noResults')}
													options={getEntitiesFromCatalog()}
													value={selectedEntity}
													onChange={(entityName) => {
														setSelectedEntity(entityName);
														setSelectedFund('');
														setSelectedPlan('');
														setSelectedDGSFPcode('');
													}}
												/>

												{selectedEntity && (
													<SearchableCombobox
														label={translate('pensionTransfer.step2.fundLabel')}
														placeholder={translate('pensionTransfer.step2.fundPlaceholder')}
														searchPlaceholder={translate('pensionTransfer.step2.fundSearch')}
														emptyText={translate('pensionTransfer.noResults')}
														options={getFundsFromCatalog(selectedEntity)}
														value={selectedFund}
														onChange={(fundName) => {
															setSelectedFund(fundName);
															setSelectedPlan('');
															setSelectedDGSFPcode('');
														}}
													/>
												)}

												{selectedFund && (
													<SearchableCombobox
														label={translate('pensionTransfer.step2.planLabel')}
														placeholder={translate('pensionTransfer.step2.planPlaceholder')}
														searchPlaceholder={translate('pensionTransfer.step2.planSearch')}
														emptyText={translate('pensionTransfer.noResults')}
														options={getPlansFromCatalog(selectedEntity, selectedFund)}
														value={selectedPlan}
														onChange={(planId) => {
															setSelectedPlan(planId);
															const plan = getPlansFromCatalog(selectedEntity, selectedFund).find(
																(p) => p.id === planId
															);
															setSelectedDGSFPcode(plan?.DGSFP_code || '');
														}}
													/>
												)}

												{selectedDGSFPcode && (
													<div className="rounded-md border border-primary/20 bg-primary/5 p-3 space-y-2">
														<p className="text-sm font-semibold text-primary">
															{translate('pensionTransfer.step2.dgsfpSelected')} {selectedDGSFPcode}
														</p>
														<Button
															size="sm"
															className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
															onClick={() => setShowIsinHelp(false)}
														>
															{translate('pensionTransfer.step2.useDgsfp')}
														</Button>
													</div>
												)}
											</>
										)}
									</div>
								)}
							</div>
						)}

						{/* Step 4 — Summary */}
						{transferStep === 4 && (
							<div className="space-y-4">
								<h3 className="text-lg font-semibold">
									{translate('pensionTransfer.step4.title')}
								</h3>
								<p className="text-sm text-muted-foreground">
									{translate('pensionTransfer.step4.description')}
								</p>
								<div className="rounded-lg border border-border bg-card p-4">
									{(() => {
										const item = catalogData.find((c) => c.DGSFP_code === selectedDGSFPcode);
										const rows = [
											{
												label: translate('pensionTransfer.step4.entity'),
												value: item?.entityName || translate('pensionTransfer.step4.unknown')
											},
											{
												label: translate('pensionTransfer.step4.fund'),
												value: item?.fundName || translate('pensionTransfer.step4.unknownFund')
											},
											{
												label: translate('pensionTransfer.step4.plan'),
												value: item?.planName || translate('pensionTransfer.step4.unknownPlan')
											},
											{
												label: translate('pensionTransfer.step4.dgsfpCode'),
												value: selectedDGSFPcode
											},
											{
												label: translate('pensionTransfer.step4.denomination'),
												value:
													item?.denomination ||
													translate('pensionTransfer.step4.unknownDenomination')
											}
										];
										return (
											<div className="space-y-3">
												{rows.map((r) => (
													<div key={r.label} className="flex items-center justify-between text-sm">
														<span className="font-medium">{r.label}</span>
														<span className="text-muted-foreground text-right max-w-[60%] truncate">
															{r.value}
														</span>
													</div>
												))}
											</div>
										);
									})()}
								</div>
							</div>
						)}

						{/* Step 5 — Success */}
						{transferStep === 5 && (
							<div className="text-center space-y-4 py-4">
								<div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
									<CheckCircle2 className="w-7 h-7 text-primary" />
								</div>
								<h3 className="text-lg font-semibold">
									{translate('pensionTransfer.step5.title')}
								</h3>
								<p className="text-sm text-muted-foreground">
									{translate('pensionTransfer.step5.requestId')} {requestId || 'N/A'}
								</p>
								<p className="text-sm text-muted-foreground">
									{translate('pensionTransfer.step5.estimatedTime')}
								</p>
								{createdTransferId && (
									<p className="text-sm font-medium text-foreground">
										{translate('pensionTransfer.step5.signPending')}
									</p>
								)}
								<button
									type="button"
									className="text-sm text-secondary hover:underline"
									onClick={() => setIsContactSupportModalOpen(true)}
								>
									{translate('pensionTransfer.step5.helpButton')}
								</button>
							</div>
						)}
					</div>

					<DialogFooter>
						{transferStep < 5 ? (
							<Button
								onClick={handleTransferContinue}
								disabled={
									(transferStep === 2 && !selectedDGSFPcode) ||
									(transferStep === 4 && (!selectedPlan || selectedPlan.trim() === ''))
								}
								className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
							>
								{transferStep === 4
									? translate('pensionTransfer.buttons.requestTransfer')
									: translate('pensionTransfer.buttons.continue')}
							</Button>
						) : (
							/* Cuando se puede firmar, firmar es lo ÚNICO que se ofrece: la
							   petición no está terminada hasta que existe el boletín, y darle
							   al cliente una salida cómoda —«volver al plan»— justo ahí es
							   invitarle a dejarla a medias (decisión de producto, 14/09/26).
							   No queda atrapado: la X del modal sigue cerrando, y quien salga
							   por ahí encuentra el paso de firma esperándole en su proceso.

							   Si NO se puede firmar (la petición nació sin plan de destino),
							   volver al plan es la única salida que tiene sentido — y sin este
							   botón la pantalla se quedaría sin ninguna. */
							createdTransferId ? (
								<Button
									onClick={handleSignBulletin}
									disabled={signingBulletin}
									data-testid="sign-bulletin-btn"
									className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
								>
									{signingBulletin
										? translate('pensionTransfer.buttons.signingBulletin')
										: translate('pensionTransfer.buttons.signBulletin')}
								</Button>
							) : (
								<Button
									onClick={handleTransferComplete}
									className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
								>
									{translate('pensionTransfer.buttons.backToDashboard')}
								</Button>
							)
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Contact support modal (kept as Chakra — shared component) */}
			<ContactSupportModal
				isOpen={isContactSupportModalOpen}
				onClose={() => setIsContactSupportModalOpen(false)}
				isMobileScreen={false}
				translate={translate}
			/>
		</>
	);
};

export default PensionPlanTransferJourneyV2;
